/**
 * AdImageUploadCard — Director chat question variant ``ad_image_upload``.
 *
 * Rendered by QuestionStack when ``question.question_type === 'ad_image_upload'``.
 * The user uploads a product reference image; the returned asset path
 * becomes the answer the Director receives.
 *
 * Falls back to a pasted URL / asset path when drag-drop is awkward —
 * see ``allow_custom_input=True`` on the backend builder.
 *
 * Build spec ref: docs/drafts/ai-ads-phase-0-1-build-spec.md §11
 * Backend builder: ``ads_tools.build_ad_image_upload_question``
 */

import { useEffect, useRef, useState } from 'react'
import { api } from '@ponpon/api-client'
import type { EmbeddedQuestion } from '@console/hooks/useAgentChat'

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; previewUrl: string; filename: string }
  | { kind: 'uploaded'; previewUrl: string; filename: string; path: string }
  | { kind: 'error'; message: string }

export function AdImageUploadCard({
  question,
  projectId,
  readOnly,
  onAnswer,
  onCustomSubmit,
  customDraft,
}: {
  question: EmbeddedQuestion
  projectId: string
  readOnly: boolean
  /** Fires once with the uploaded asset path; QuestionStack auto-submits. */
  onAnswer: (path: string) => void
  /** Pasted URL / asset path fallback — fires on Enter in the text input. */
  onCustomSubmit: (text: string) => void
  customDraft: string
}) {
  const [state, setState] = useState<UploadState>({ kind: 'idle' })
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Revoke the previous blob URL whenever a new one supersedes it, and
  // on unmount. Without this each upload leaks ~kB of blob storage per
  // file plus a GC root on the underlying File object — small per render
  // but unbounded across a long Director session.
  useEffect(() => {
    const previewUrl =
      state.kind === 'uploading' || state.kind === 'uploaded'
        ? state.previewUrl
        : null
    if (!previewUrl) return
    return () => {
      URL.revokeObjectURL(previewUrl)
    }
  }, [state])

  const accept = question.metadata?.accept ?? 'image/png,image/jpeg,image/webp'

  const answered = question.answer != null
  const disabled = readOnly || answered || state.kind === 'uploading'

  async function handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      setState({
        kind: 'error',
        message: `Not an image: ${file.type || 'unknown'}`,
      })
      return
    }
    const previewUrl = URL.createObjectURL(file)
    setState({ kind: 'uploading', previewUrl, filename: file.name })
    try {
      const result = await api.uploadAsset(projectId, file)
      setState({
        kind: 'uploaded',
        previewUrl,
        filename: file.name,
        path: result.path,
      })
      onAnswer(result.path)
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : String(err)
      setState({ kind: 'error', message })
    }
  }

  function openFilePicker() {
    fileInputRef.current?.click()
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      void handleFile(file)
    }
    // Reset so picking the same file twice still fires onChange.
    e.target.value = ''
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragging(false)
    if (disabled) return
    const file = e.dataTransfer.files?.[0]
    if (file) {
      void handleFile(file)
    }
  }

  return (
    <div
      className="rounded-md border border-line-2 bg-bg-2/60 px-3.5 py-3"
      data-testid="ad-image-upload-card"
    >
      {question.header && (
        <div className="t-mono text-[0.62rem] uppercase tracking-[0.18em] text-accent mb-1">
          {question.header}
        </div>
      )}
      <div className="text-text text-[0.92rem] leading-snug mb-2.5">
        {question.question}
      </div>

      {answered ? (
        <div className="rounded-md border border-line-2 bg-white/[0.02] px-3.5 py-2.5 text-text-3 text-[0.82rem] italic">
          Uploaded: <code className="text-text">{question.answer}</code>
        </div>
      ) : state.kind === 'uploaded' ? (
        <div className="flex items-start gap-3">
          <img
            src={state.previewUrl}
            alt={state.filename}
            className="w-16 h-16 rounded-md object-cover border border-line-2"
          />
          <div className="flex-1">
            <div className="text-text text-[0.9rem] font-medium leading-tight">
              {state.filename}
            </div>
            <div className="text-text-3 text-[0.78rem] mt-1">
              Uploaded — Director will use this as the product reference.
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={disabled ? undefined : openFilePicker}
            onKeyDown={(e) => {
              if (disabled) return
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openFilePicker()
              }
            }}
            onDragOver={(e) => {
              e.preventDefault()
              if (!disabled) setIsDragging(true)
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            className={`rounded-md border border-dashed px-3.5 py-5 text-center transition-all ${
              isDragging
                ? 'border-accent bg-accent/[0.06]'
                : disabled
                  ? 'border-line-2 bg-white/[0.02] opacity-50 cursor-default'
                  : 'border-line-2 bg-white/[0.02] hover:bg-white/[0.04] hover:border-line-3 cursor-pointer'
            }`}
          >
            {state.kind === 'uploading' ? (
              <div className="flex items-center justify-center gap-3">
                <img
                  src={state.previewUrl}
                  alt={state.filename}
                  className="w-12 h-12 rounded-md object-cover border border-line-2"
                />
                <div className="text-text-3 text-[0.86rem]">
                  Uploading {state.filename}…
                </div>
              </div>
            ) : (
              <>
                <div className="text-text text-[0.9rem] font-medium">
                  Drop an image here, or click to browse
                </div>
                <div className="text-text-3 text-[0.78rem] mt-1">
                  PNG, JPEG, or WebP — square works best
                </div>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={onInputChange}
            disabled={disabled}
            style={{ display: 'none' }}
            data-testid="ad-image-upload-input"
          />
        </>
      )}

      {state.kind === 'error' && (
        <div className="mt-2 rounded-md border border-red-500/40 bg-red-500/[0.08] px-3 py-2 text-red-300 text-[0.82rem]">
          {state.message}
        </div>
      )}

      {/* URL / asset-path fallback. Kept enabled even after a successful
          upload so the user can correct a wrong file by pasting a path
          before the Director has fired — QuestionStack treats this as a
          custom-text submit on Enter. */}
      {!answered && question.allow_custom_input && (
        <div className="mt-2">
          <input
            type="text"
            placeholder="Or paste a URL / asset path…"
            defaultValue={customDraft}
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const text = (e.target as HTMLInputElement).value.trim()
                if (text) onCustomSubmit(text)
              }
            }}
            className="field text-[0.86rem] w-full"
            style={{ padding: '0.5rem 0.7rem' }}
          />
        </div>
      )}
    </div>
  )
}
